import { createContext, type JSX, type ReactNode, useContext } from 'react';
import { DEFAULT_SOURCE, type Source } from '../../src/source';

/**
 * Which series the page under this provider is reading — the one filter that is not view state.
 *
 * **Why this is a context and not a field on `ViewState`.** Every other filter on this site round
 * trips through the query string: a window, a prefecture, a sort order are all things a reader
 * chooses and shares. The series is not. `/liiklusonnetused` *is* the Liiklusinfo log the way
 * `/vargused` *is* the digest's theft category — it is the identity of the page, and a reader who
 * could type `?source=digest` onto the traffic page would get a page whose Estonian caption names
 * one series and whose numbers come from the other. That is the single failure this whole epic
 * exists to prevent, arriving through the one input the type system does not control.
 *
 * So it is passed down the tree instead, where a URL cannot reach it. `tests/use-url-state.test.ts`
 * asserts `encodeViewState` never emits a `source=` parameter, which is the other half of the
 * guarantee: not merely that nothing writes it, but that nothing can.
 *
 * **Why a context rather than a prop.** `TimeSeriesChart`, `IncidentFeed` and `AggregateChart` all
 * build their own request from `(category, state)` — deliberately, so a panel's query can never
 * describe a window its own caption does not. Threading a fourth argument through each of them
 * would put the series in the hands of every call site, and a page with six panels is six chances
 * to pass the wrong one. One provider at the top of the page is one place to be right.
 *
 * The default is the digest, for the same reason `DEFAULT_SOURCE` is: every page that existed
 * before this context reads that series, and none of them are wrapped in a provider. They keep
 * working, unchanged, and say so in their own captions.
 */
const SourceContext = createContext<Source>(DEFAULT_SOURCE);

/** Names the series for everything rendered inside it. One per page, at the top. */
export function SourceProvider({
  source,
  children,
}: {
  source: Source;
  // Optional so `createElement(SourceProvider, {source}, child)` — how the tests build trees, with
  // no JSX under the repo's node-only vitest config — satisfies the props type.
  children?: ReactNode;
}): JSX.Element {
  return <SourceContext.Provider value={source}>{children}</SourceContext.Provider>;
}

/**
 * The series the surrounding page reads.
 *
 * Every panel that builds a request asks this rather than taking it as a prop, so a panel and the
 * page it sits on cannot disagree about which corpus is on screen.
 */
export function useSource(): Source {
  return useContext(SourceContext);
}
